import { useRef, useState } from "react";
import { Link } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { SHOP_TYPES, type ShopType, type VehicleCatalogRefreshResult } from "@autorepair/contracts";
import { adminApi, ApiError } from "../lib/api";
import { formatDate } from "../lib/money";
import { COUNTRY_NAMES, SUPPORTED_COUNTRY_CODES, guessCountryFromLocale, type CountryCode } from "../lib/geo";
import { useNotice } from "../lib/useNotice";
import { Spinner } from "../components/ui-modern";
import { TenantLegalModal } from "../components/TenantLegalModal";
import {
  Eyebrow,
  ModernButton,
  ModernCard,
  ModernConfirmButton,
  ModernEmptyState,
  ModernErrorBanner,
  ModernField,
  ModernInput,
  ModernModal,
  ModernNoticeBanner,
  ModernSelect,
  ModernSuccessBanner,
} from "../components/ui-modern";

const SHOP_TYPE_LABELS: Record<ShopType, string> = { auto_repair: "Auto Repair", detailing: "Detailing" };

/** Modern counterpart of AdminPage.tsx's own `GeneratedCredentialsModal` —
 * identical contract/behavior, restyled with ui-modern.tsx's primitives.
 * Not shared with Classic's version (a small, private, one-off modal —
 * duplicating it here is cheaper than making it theme-aware for a single
 * call site, same tradeoff every other screen's own private modals make). */
function GeneratedCredentialsModal({
  title,
  result,
  onClose,
}: {
  title: string;
  result: { ownerEmail: string; temporaryPassword: string };
  onClose: () => void;
}) {
  const [acknowledged, setAcknowledged] = useState(false);
  return (
    <ModernModal title={title} onCancel={onClose}>
      <p className="mb-3 text-[13.5px] text-[#4a5361]">
        Hand these to the owner directly — the password won't be shown again. They'll be required to set their own
        password the moment they next log in.
      </p>
      <div className="mb-4 flex flex-col gap-2 rounded-[10px] border border-[#e2e7ee] bg-[#fbfcfe] p-4 font-mono text-[14px] text-[#101b2d]">
        <div>
          <span className="text-[12.5px] text-[#6b7a8f]">Email: </span>
          {result.ownerEmail}
        </div>
        <div>
          <span className="text-[12.5px] text-[#6b7a8f]">Temporary Password: </span>
          {result.temporaryPassword}
        </div>
      </div>
      <label className="mb-4 flex items-center gap-2 text-[13.5px] text-[#4a5361]">
        <input
          type="checkbox"
          checked={acknowledged}
          onChange={(e) => setAcknowledged(e.target.checked)}
          className="h-4 w-4 rounded border border-[#e2e7ee]"
        />
        I've handed these to the new owner.
      </label>
      <ModernButton type="button" className="w-full" disabled={!acknowledged} onClick={onClose}>
        Done
      </ModernButton>
    </ModernModal>
  );
}

/**
 * Upload a make/model/year/trim CSV to refresh the global vehicle catalog
 * (vehicle_makes/vehicle_models/vehicle_model_years/vehicle_trims — see
 * AdminVehicleCatalogService's own doc comment). The whole file is
 * validated server-side before anything is applied: a bad file comes back
 * with `applied: false` and a list of per-row problems, and NOTHING
 * touches the database until a re-upload comes back clean. Always
 * additive — a make/model/year/trim missing from this file is left alone,
 * never removed.
 */
function VehicleCatalogCard() {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [file, setFile] = useState<File | null>(null);
  const [result, setResult] = useState<VehicleCatalogRefreshResult | null>(null);
  const [error, setError] = useState<string | null>(null);

  function clearSelection(): void {
    setFile(null);
    if (fileInputRef.current) fileInputRef.current.value = "";
  }

  const refreshMutation = useMutation({
    mutationFn: () => {
      if (!file) throw new Error("Choose a CSV file first.");
      return adminApi.refreshVehicleCatalog(file);
    },
    onSuccess: (res) => {
      setResult(res);
      setError(null);
      if (res.applied) clearSelection();
    },
    onError: (err) => {
      setResult(null);
      setError(err instanceof ApiError || err instanceof Error ? err.message : "Could not refresh the vehicle catalog.");
    },
  });

  return (
    <ModernCard className="mb-4">
      <h2 className="m-0 mb-1 text-[15px] font-bold text-[#071a33]">Vehicle catalog</h2>
      <p className="m-0 mb-4 text-[12.5px] text-[#6b7a8f]">
        Upload a CSV with <code>make,model,year,trim</code> columns (trim may be blank) to add or update the shared
        make/model/year/trim picker every shop uses. The whole file is checked for errors first — nothing is applied
        until a file comes back clean. Existing entries not present in the file are left alone, never removed.
      </p>
      {error && <ModernErrorBanner message={error} />}
      {result && !result.applied && (
        <div className="mb-4 rounded-[10px] border border-[#f3d7d5] bg-[#fdecea] px-4 py-3 text-[13.5px] text-[#b3261e]">
          <p className="m-0 mb-2 font-bold">
            Nothing was applied — {result.errors.length} problem{result.errors.length === 1 ? "" : "s"} found:
          </p>
          <ul className="m-0 list-disc pl-5">
            {result.errors.map((msg, i) => (
              <li key={i}>{msg}</li>
            ))}
          </ul>
          {result.truncatedErrors && <p className="m-0 mt-2 italic">…and more. Fix these and re-upload to see the rest.</p>}
        </div>
      )}
      {result?.applied && result.summary && (
        <ModernSuccessBanner
          message={`Applied: ${result.summary.makesProcessed} make(s), ${result.summary.modelsProcessed} model(s), ${result.summary.modelYearsAdded} new model-year row(s), ${result.summary.trimsProcessed} trim(s) — from ${result.summary.dataRowsRead} data row(s) read.`}
        />
      )}
      <input ref={fileInputRef} type="file" accept=".csv,text/csv" onChange={(e) => setFile(e.target.files?.[0] ?? null)} className="hidden" />
      <div className="flex flex-wrap items-center gap-2">
        {!file && (
          <ModernButton type="button" variant="secondary" onClick={() => fileInputRef.current?.click()}>
            Choose CSV File
          </ModernButton>
        )}
        {file && (
          <>
            <span className="text-[13px] text-[#4a5361]">{file.name}</span>
            <ModernButton type="button" onClick={() => refreshMutation.mutate()} disabled={refreshMutation.isPending}>
              {refreshMutation.isPending ? "Validating & applying…" : "Validate & Apply"}
            </ModernButton>
            <ModernButton type="button" variant="secondary" disabled={refreshMutation.isPending} onClick={clearSelection}>
              Cancel
            </ModernButton>
          </>
        )}
      </div>
    </ModernCard>
  );
}

/**
 * The Admin Panel screen — see docs/FRONTEND_BACKLOG.md Round 62 (owner
 * profile / admin panel, the last 2 screens outside the original
 * 11-mockup rollout). No mockup screen exists for this page — it follows
 * SettingsPageModern.tsx's own card-per-section layout instead.
 */
export function AdminPageModern() {
  const queryClient = useQueryClient();
  const [shopName, setShopName] = useState("");
  const [ownerFullName, setOwnerFullName] = useState("");
  const [ownerEmail, setOwnerEmail] = useState("");
  // Pre-filled from the admin's OWN browser locale as a starting guess,
  // not the new shop's — there's no signal for the shop's actual location
  // any earlier than this form, and it's editable before submitting. See
  // guessCountryFromLocale()'s own doc comment.
  const [country, setCountry] = useState<CountryCode>(() => guessCountryFromLocale());
  const [shopType, setShopType] = useState<ShopType>("auto_repair");
  const [formError, setFormError] = useState<string | null>(null);
  const listNotice = useNotice();
  const [credentialsModal, setCredentialsModal] = useState<{
    title: string;
    result: { ownerEmail: string; temporaryPassword: string };
  } | null>(null);

  const [legalModal, setLegalModal] = useState<{ tenantId: number; shopName: string } | null>(null);

  const tenantsQuery = useQuery({ queryKey: ["admin", "tenants"], queryFn: adminApi.listTenants });

  const createMutation = useMutation({
    mutationFn: () => adminApi.createTenant({ shopName, ownerFullName, ownerEmail, country, shopType }),
    onSuccess: (result) => {
      setCredentialsModal({ title: "Shop Created", result });
      setShopName("");
      setOwnerFullName("");
      setOwnerEmail("");
      setCountry(guessCountryFromLocale());
      void queryClient.invalidateQueries({ queryKey: ["admin", "tenants"] });
    },
    onError: (err) => setFormError(err instanceof ApiError ? err.message : "Could not create this shop."),
  });

  const lockMutation = useMutation({
    mutationFn: (tenantId: number) => adminApi.lockTenant(tenantId),
    onSuccess: () => {
      listNotice.showSuccess("Shop locked — every user there is signed out.");
      void queryClient.invalidateQueries({ queryKey: ["admin", "tenants"] });
    },
    onError: (err) => listNotice.showError(err instanceof ApiError ? err.message : "Could not lock this shop."),
  });

  const unlockMutation = useMutation({
    mutationFn: (tenantId: number) => adminApi.unlockTenant(tenantId),
    onSuccess: () => {
      listNotice.showSuccess("Shop unlocked.");
      void queryClient.invalidateQueries({ queryKey: ["admin", "tenants"] });
    },
    onError: (err) => listNotice.showError(err instanceof ApiError ? err.message : "Could not unlock this shop."),
  });

  const resetPasswordMutation = useMutation({
    mutationFn: (tenantId: number) => adminApi.resetTenantPassword(tenantId),
    onSuccess: (result) => setCredentialsModal({ title: "Password Reset", result }),
    onError: (err) => listNotice.showError(err instanceof ApiError ? err.message : "Could not reset this shop's password."),
  });

  const disableTotpMutation = useMutation({
    mutationFn: (tenantId: number) => adminApi.disableTenantTotp(tenantId),
    onSuccess: () => listNotice.showSuccess("Two-factor disabled for this shop's owner."),
    onError: (err) => listNotice.showError(err instanceof ApiError ? err.message : "Could not disable two-factor for this shop."),
  });

  // Admin-only, never owner/staff-editable — see tenants.shop_type's own
  // doc comment (db/schema/tenancy.ts) and docs/SHOP_TYPES_PLAN.md.
  const shopTypeMutation = useMutation({
    mutationFn: ({ tenantId, shopType }: { tenantId: number; shopType: ShopType }) =>
      adminApi.setTenantTheme(tenantId, { shopType }),
    onSuccess: () => {
      listNotice.showSuccess("Shop type updated.");
      void queryClient.invalidateQueries({ queryKey: ["admin", "tenants"] });
    },
    onError: (err) => listNotice.showError(err instanceof ApiError ? err.message : "Could not update this shop's type."),
  });

  return (
    <div>
      <div className="mb-5">
        <Eyebrow className="mb-2 text-[11px] text-[#0f6d86]">Platform admin</Eyebrow>
        <h1 className="m-0 text-[28px] font-bold text-[#071a33]">Admin panel</h1>
      </div>

      <ModernCard className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="m-0 mb-1 text-[15px] font-bold text-[#071a33]">Support Inquiries</h2>
          <p className="m-0 text-[12.5px] text-[#6b7a8f]">Contact-form submissions from bay2bill-web and the app's own Support page.</p>
        </div>
        <Link to="/admin/support">
          <ModernButton type="button" variant="secondary">
            Open inbox
          </ModernButton>
        </Link>
      </ModernCard>

      <ModernCard className="mb-4">
        <h2 className="m-0 mb-1 text-[15px] font-bold text-[#071a33]">Create a new shop</h2>
        <p className="m-0 mb-4 text-[12.5px] text-[#6b7a8f]">
          Sales tax, shop supplies, and invoice wording all start from the same defaults every shop already uses — the
          new owner can change any of it afterward from Shop Settings.
        </p>
        {formError && <ModernErrorBanner message={formError} />}
        <form
          className="grid grid-cols-1 gap-3.5 sm:grid-cols-3 sm:items-start"
          onSubmit={(e) => {
            e.preventDefault();
            createMutation.mutate();
          }}
        >
          <ModernField label="Shop name" htmlFor="m-shopName" required>
            <ModernInput id="m-shopName" required autoFocus value={shopName} onChange={(e) => setShopName(e.target.value)} />
          </ModernField>
          <ModernField label="Owner full name" htmlFor="m-ownerFullName" required>
            <ModernInput id="m-ownerFullName" required value={ownerFullName} onChange={(e) => setOwnerFullName(e.target.value)} />
          </ModernField>
          <ModernField label="Owner email" htmlFor="m-ownerEmail" required>
            <ModernInput
              id="m-ownerEmail"
              type="email"
              required
              value={ownerEmail}
              onChange={(e) => setOwnerEmail(e.target.value)}
            />
          </ModernField>
          <ModernField label="Country" htmlFor="m-country">
            <ModernSelect id="m-country" value={country} onChange={(e) => setCountry(e.target.value as CountryCode)}>
              {SUPPORTED_COUNTRY_CODES.map((code) => (
                <option key={code} value={code}>
                  {COUNTRY_NAMES[code]}
                </option>
              ))}
            </ModernSelect>
          </ModernField>
          <ModernField label="Shop type" htmlFor="m-shopType">
            <ModernSelect id="m-shopType" value={shopType} onChange={(e) => setShopType(e.target.value as ShopType)}>
              {SHOP_TYPES.map((type) => (
                <option key={type} value={type}>
                  {SHOP_TYPE_LABELS[type]}
                </option>
              ))}
            </ModernSelect>
          </ModernField>
          <ModernButton
            type="submit"
            className="sm:col-span-3 sm:w-auto sm:justify-self-start"
            disabled={createMutation.isPending || !shopName.trim() || !ownerFullName.trim() || !ownerEmail.trim()}
          >
            {createMutation.isPending ? "Creating…" : "Create Shop & Generate Password"}
          </ModernButton>
        </form>
      </ModernCard>

      <VehicleCatalogCard />

      <h2 className="m-0 mb-3 text-[15px] font-bold text-[#071a33]">Shops</h2>
      <ModernNoticeBanner notice={listNotice.notice} />
      {tenantsQuery.isLoading && <Spinner />}
      {tenantsQuery.error && <ModernErrorBanner message="Could not load shops." />}
      {tenantsQuery.data?.items.length === 0 && <ModernEmptyState>No shops yet.</ModernEmptyState>}
      <div className="flex flex-col gap-3">
        {tenantsQuery.data?.items.map((t) => (
          <ModernCard key={t.tenantId} className="flex flex-wrap items-center justify-between gap-3">
            <div>
              <div className="flex items-center gap-2.5">
                <span className="text-[15px] font-bold text-[#071a33]">{t.displayName}</span>
                {!t.isActive && <span className="text-[12.5px] font-bold text-[#b3261e]">Locked</span>}
              </div>
              <div className="text-[12.5px] text-[#6b7a8f]">
                {t.ownerEmail ?? "No owner"} · Created {formatDate(t.createdAt)}
              </div>
              <div className="text-[12.5px] text-[#6b7a8f]">
                {t.customerCount} customers · {t.workOrderCount} work orders · {t.invoiceCount} invoices ·{" "}
                {t.technicianCount} technicians · {t.predefinedItemCount} predefined items
              </div>
            </div>
            <div className="flex flex-wrap items-center gap-2.5">
              <label className="flex items-center gap-2 text-[12.5px] text-[#4a5361]">
                Shop type
                <ModernSelect
                  className="h-9 w-auto min-w-0 py-0 text-[13px]"
                  aria-label={`Shop type for ${t.displayName}`}
                  value={t.shopType}
                  disabled={shopTypeMutation.isPending}
                  onChange={(e) => shopTypeMutation.mutate({ tenantId: t.tenantId, shopType: e.target.value as ShopType })}
                >
                  {SHOP_TYPES.map((shopType) => (
                    <option key={shopType} value={shopType}>
                      {SHOP_TYPE_LABELS[shopType]}
                    </option>
                  ))}
                </ModernSelect>
              </label>
              <ModernButton
                type="button"
                variant="secondary"
                className="min-h-0 px-3.5 py-2 text-[13px]"
                onClick={() => setLegalModal({ tenantId: t.tenantId, shopName: t.displayName })}
              >
                Legal
              </ModernButton>
              <ModernConfirmButton
                variant="secondary"
                className="min-h-0 px-3.5 py-2 text-[13px]"
                confirmText="Reset right away and generate a new password? The current one stops working immediately."
                disabled={resetPasswordMutation.isPending || !t.ownerEmail}
                onConfirm={() => resetPasswordMutation.mutate(t.tenantId)}
              >
                Reset Password
              </ModernConfirmButton>
              {t.ownerTotpEnabled && (
                <ModernConfirmButton
                  variant="secondary"
                  className="min-h-0 px-3.5 py-2 text-[13px]"
                  confirmText="Disable this owner's two-factor right away? They'll be able to log in with just a password until they re-enroll."
                  disabled={disableTotpMutation.isPending}
                  onConfirm={() => disableTotpMutation.mutate(t.tenantId)}
                >
                  Disable 2FA
                </ModernConfirmButton>
              )}
              {t.isActive ? (
                <ModernConfirmButton
                  variant="danger"
                  className="min-h-0 px-3.5 py-2 text-[13px]"
                  confirmText="Lock this shop? Every user there is signed out immediately."
                  disabled={lockMutation.isPending}
                  onConfirm={() => lockMutation.mutate(t.tenantId)}
                >
                  Lock
                </ModernConfirmButton>
              ) : (
                <ModernButton
                  type="button"
                  variant="secondary"
                  className="min-h-0 px-3.5 py-2 text-[13px]"
                  disabled={unlockMutation.isPending}
                  onClick={() => unlockMutation.mutate(t.tenantId)}
                >
                  Unlock
                </ModernButton>
              )}
            </div>
          </ModernCard>
        ))}
      </div>

      {legalModal && (
        <TenantLegalModal tenantId={legalModal.tenantId} shopName={legalModal.shopName} onClose={() => setLegalModal(null)} />
      )}

      {credentialsModal && (
        <GeneratedCredentialsModal
          title={credentialsModal.title}
          result={credentialsModal.result}
          onClose={() => setCredentialsModal(null)}
        />
      )}
    </div>
  );
}
